import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { FaArrowLeft, FaShare, FaEye, FaEdit, FaTrash, FaClock } from 'react-icons/fa';
import { toast } from 'sonner';
import { API, BACKEND } from '../App';
import { useAuth } from '../context/AuthContext';
import { getAnonymousClientId } from '../utils/guides';
import VideoEmbed from '../components/VideoEmbed';
import { LikeButton } from '../components/LikeButton';
import { ShareModal } from '../components/ShareModal';

const CATEGORY_LABELS = {
  maskiner: 'Maskiner',
  produkter: 'Produkter & Ingredienser',
  rengoring: 'Rengøring & Vedligehold',
  teknik: 'Teknik & Udstyr',
  brugertips: 'Brugertips & Erfaringer',
  tilbehor: 'Tilbehør & Servering',
};

const GuideDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Fetch by slug
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const { data } = await axios.get(`${API}/tips/by-slug/${slug}`);
        if (cancelled) return;
        setGuide(data);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          console.error('[GuideDetail] fetch failed', err);
          toast.error('Kunne ikke indlæse guiden.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Ping view-count (fire-and-forget, dedupped server-side per client_id/day)
  useEffect(() => {
    if (!guide?.id) return;
    const cid = getAnonymousClientId();
    axios.post(`${API}/tips/${guide.id}/view`, { client_id: cid })
      .catch(() => { /* non-critical */ });
  }, [guide?.id]);

  const canonical = useMemo(() => {
    if (!guide?.slug) return '';
    return `${window.location.origin}/guides/${guide.slug}`;
  }, [guide?.slug]);

  // Post-process links i body_rich: interne SlushBook-links skal åbne i
  // samme fane (som en normal navigation) — eksterne beholder target=_blank.
  // Klik på interne links routes via React Router (client-side) så SPA'en
  // ikke laver full page reload.
  const bodyRef = useRef(null);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || !guide) return;

    const links = el.querySelectorAll('a[href]');
    links.forEach((a) => {
      const href = a.getAttribute('href') || '';
      const isInternal =
        href.startsWith('/') ||
        (() => {
          try { return new URL(href, window.location.href).host === window.location.host; }
          catch { return false; }
        })();
      if (isInternal) {
        a.removeAttribute('target');
        a.removeAttribute('rel');
        a.classList.add('guide-internal-link');
      } else {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      }
    });

    // Delegated click handler: interne links routes via React Router.
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a || !el.contains(a)) return;
      const href = a.getAttribute('href') || '';
      // Ignore modifier clicks so cmd/ctrl+click åbner i ny fane som normalt.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      let isInternal = false;
      let path = '';
      if (href.startsWith('/')) {
        isInternal = true; path = href;
      } else {
        try {
          const u = new URL(href, window.location.href);
          if (u.host === window.location.host) { isInternal = true; path = u.pathname + u.search + u.hash; }
        } catch { /* noop */ }
      }
      if (isInternal && path) {
        e.preventDefault();
        navigate(path);
      }
    };
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [guide, navigate]);

  // Absolute image URL for OG-tags — Helmet renders these for browsers, but
  // for actual Facebook crawler sharing use the /api/share/guide/{slug} URL.
  const absImage = useMemo(() => {
    if (!guide?.image_url) return null;
    if (guide.image_url.startsWith('http')) return guide.image_url;
    return `${BACKEND}${guide.image_url}`;
  }, [guide?.image_url]);

  const isAuthor = user && guide && user.id === guide.created_by;
  const isEditor = user && (user.role === 'admin' || user.role === 'editor');

  const [showShareModal, setShowShareModal] = useState(false);
  const handleShare = useCallback(async () => {
    if (!guide?.slug) return;
    // Public share URL points to the OG-tagged backend endpoint. Både OG-crawlere
    // (Facebook, Twitter, WhatsApp) og menneskelige brugere lander korrekt:
    // crawlere ser rige previews, mennesker redirectes øjeblikkeligt til
    // /guides/{slug}.
    const shareUrl = `${BACKEND}/api/share/guide/${guide.slug}`;
    const shareData = {
      title: guide.title,
      text: guide.short_description || '',
      url: shareUrl,
    };
    // Foretræk native share sheet på mobile/tablet — fald tilbage til modal.
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        console.warn('navigator.share failed, falling back to modal:', err);
      }
    }
    setShowShareModal(true);
  }, [guide]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = useCallback(async () => {
    if (!guide?.id) return;
    const confirmMsg =
      `Slet guiden "${guide.title}"?\n\n` +
      `Dette fjerner både guiden og alle tilknyttede billeder (cover + inline).\n` +
      `Handlingen kan ikke fortrydes.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      setDeleting(true);
      await axios.delete(`${API}/tips/${guide.id}`);
      toast.success('🗑️ Guiden er slettet');
      navigate('/guides');
    } catch (err) {
      console.error('[GuideDetail] delete failed', err);
      const detail = err.response?.data?.detail || 'Kunne ikke slette guiden. Prøv igen.';
      toast.error(detail);
      setDeleting(false);
    }
  }, [guide, navigate]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center text-gray-500" data-testid="guide-detail-loading">
        Indlæser guide…
      </div>
    );
  }

  if (notFound || !guide) {
    return (
      <div className="max-w-3xl mx-auto p-6" data-testid="guide-detail-not-found">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Guiden findes ikke</h1>
        <p className="text-gray-600 mb-6">Det ser ud til, at guiden er blevet fjernet, eller at linket er forkert.</p>
        <Link to="/guides" className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600">
          <FaArrowLeft /> Se alle guides
        </Link>
      </div>
    );
  }

  const bodyHtml = guide.body_rich || (guide.content ? `<p>${_escapeHtml(guide.content)}</p>` : '');
  const createdDate = _fmtDate(guide.created_at);
  const updatedDate = guide.updated_at ? _fmtDate(guide.updated_at) : null;
  // "Senest opdateret" er primær visning i vidensbasen. Kun hvis guiden
  // aldrig er redigeret (opdateret == oprettelse eller mangler) viser vi
  // "Oprettet" — så nye guides ikke ser forladte ud.
  const wasEdited = updatedDate && updatedDate !== createdDate;
  const primaryDateLabel = wasEdited ? 'Senest opdateret' : 'Oprettet';
  const primaryDateValue = wasEdited ? updatedDate : createdDate;

  // Forfatter: 'Admin' er en intern systemrolle og bør ikke vises som forfatter
  // udadtil. Fald tilbage til 'SlushBook' som brand-agnostisk visning.
  const rawAuthor = (guide.creator_name || '').trim();
  const isSystemRoleName = /^(admin|administrator|root|system)$/i.test(rawAuthor);
  const displayAuthor = (!rawAuthor || isSystemRoleName) ? 'SlushBook' : rawAuthor;

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="guide-detail-page" data-slug={guide.slug}>
      {/* Meta tags — for browsers + tab title. Facebook uses /api/share/... */}
      <Helmet>
        <title>{guide.title} — Tips & Guides</title>
        {guide.short_description && <meta name="description" content={guide.short_description} />}
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={guide.title} />
        {guide.short_description && <meta property="og:description" content={guide.short_description} />}
        <meta property="og:url" content={canonical} />
        {absImage && <meta property="og:image" content={absImage} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={guide.title} />
        {guide.short_description && <meta name="twitter:description" content={guide.short_description} />}
        {absImage && <meta name="twitter:image" content={absImage} />}
      </Helmet>

      <div className="flex items-center justify-between mb-4">
        <Link to="/guides" className="inline-flex items-center gap-2 text-cyan-700 hover:text-cyan-800 text-sm">
          <FaArrowLeft /> Alle guides
        </Link>
        <div className="flex items-center gap-2">
          {(isAuthor || isEditor) && (
            <Link
              to={`/tips/${guide.slug}/edit`}
              className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700"
              data-testid="guide-detail-edit-link"
            >
              <FaEdit /> Rediger
            </Link>
          )}
          {(isAuthor || isEditor) && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center gap-2 px-3 py-2 bg-red-100 hover:bg-red-200 rounded-lg text-sm text-red-700 disabled:opacity-50"
              data-testid="guide-detail-delete-btn"
            >
              <FaTrash /> {deleting ? 'Sletter…' : 'Slet'}
            </button>
          )}
          <LikeButton
            contentType="guide"
            contentId={guide.id}
            initialCount={guide.like_count ?? guide.likes ?? 0}
            initialLiked={!!guide.user_liked}
            contentTitle={guide.title}
            contentCategory={guide.category}
            size="md"
          />
          <button
            type="button"
            onClick={handleShare}
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg text-sm text-blue-700"
            data-testid="guide-detail-share-btn"
          >
            <FaShare /> Del
          </button>
        </div>
      </div>

      <article className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Cover */}
        {guide.image_url && (
          <img
            src={absImage}
            alt={guide.title}
            className="w-full h-auto"
            data-testid="guide-detail-cover"
          />
        )}

        <div className="p-6 sm:p-8">
          {/* Category badge */}
          {guide.category && (
            <span className="inline-block text-xs uppercase tracking-wide text-cyan-700 bg-cyan-50 px-2 py-1 rounded mb-3">
              {CATEGORY_LABELS[guide.category] || guide.category}
            </span>
          )}
          {/* Draft badge */}
          {guide.status === 'draft' && (
            <span className="inline-block ml-2 text-xs uppercase tracking-wide text-amber-800 bg-amber-100 px-2 py-1 rounded mb-3">
              Kladde
            </span>
          )}

          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" data-testid="guide-detail-title">
            {guide.title}
          </h1>

          {guide.short_description && (
            <p className="text-lg text-gray-600 mb-4" data-testid="guide-detail-short-description">
              {guide.short_description}
            </p>
          )}

          {/* Meta line — forfatter + primær dato + views + likes */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mb-8 border-b border-gray-100 pb-4">
            <span data-testid="guide-detail-author">
              Af <strong className="text-gray-700">{displayAuthor}</strong>
            </span>
            {primaryDateValue && (
              <span data-testid="guide-detail-primary-date" className="inline-flex items-center gap-1">
                <FaClock /> {primaryDateLabel} {primaryDateValue}
              </span>
            )}
            <span data-testid="guide-detail-views" className="inline-flex items-center gap-1">
              <FaEye /> {guide.views_count || 0}
            </span>
          </div>

          {/* Video embed — med kilde-caption under */}
          {guide.video_url && <VideoEmbed url={guide.video_url} title={guide.title} showSource />}

          {/* Body */}
          <div
            ref={bodyRef}
            className="guide-body prose prose-sm sm:prose-base max-w-none"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
            data-testid="guide-detail-body"
          />
        </div>
      </article>

      <ShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareUrl={guide?.slug ? `${BACKEND}/api/share/guide/${guide.slug}` : ''}
        shareTitle={guide?.title ? `${guide.title} · SlushBook` : 'SlushBook guide'}
        shareText={guide?.short_description || ''}
        heading="Del guiden"
        subheading="Del denne guide med venner og familie. Modtagere kan åbne den uden at logge ind."
      />
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────────

function _fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return d.toLocaleDateString('da-DK', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
}

function _escapeHtml(s) {
  const div = document.createElement('div');
  div.textContent = s || '';
  return div.innerHTML;
}

export default GuideDetailPage;
